import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { bidService } from '../services/bidService';
import { BidUploadComponent } from '../components/BidUploadComponent';
import { Plus, FileText, Search, Filter, Calendar, Loader2, ChevronLeft, ChevronRight, FileCheck, AlertCircle } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { BidRegistryItem } from '../models/bid';

// Simple input component
const Input = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => {
  return (
    <input
      ref={ref}
      className={`flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    />
  )
})
Input.displayName = "Input"

export const BidsPage: React.FC = () => {
  const [bids, setBids] = useState<BidRegistryItem[]>([]);
  const [filteredBids, setFilteredBids] = useState<BidRegistryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Sort options
  const [sortOption, setSortOption] = useState<'newest' | 'oldest'>('newest');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Pagination options
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageSizeDropdownOpen, setPageSizeDropdownOpen] = useState(false);
  const pageSizeDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
      if (pageSizeDropdownRef.current && !pageSizeDropdownRef.current.contains(event.target as Node)) {
        setPageSizeDropdownOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    loadBidRegistry();
  }, []);

  // Filter and sort bids
  useEffect(() => {
    let filtered = [...bids];

    // Apply search filter (currently just by date, can extend to search by extracted data)
    if (searchQuery) {
      filtered = filtered.filter(bid =>
        new Date(bid.date).toLocaleDateString().toLowerCase().includes(searchQuery.toLowerCase()) ||
        bid.status.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Sort the filtered results
    switch (sortOption) {
      case 'newest':
        filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        break;
      case 'oldest':
        filtered.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        break;
    }

    setFilteredBids(filtered);
    setCurrentPage(1); // Reset to first page when filtering
  }, [bids, searchQuery, sortOption]);

  const loadBidRegistry = async () => {
    try {
      setLoading(true);
      const registry = await bidService.getBidRegistry();
      setBids(registry.bidRegistry || []);
    } catch (error) {
      console.error('Error loading bid registry:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBidUploaded = (bidId: string) => {
    setIsUploadDialogOpen(false);
    loadBidRegistry(); // Reload to show the new bid
    navigate(`/bids/${bidId}`);
  };

  const handleBidClick = (bidId: string) => {
    navigate(`/bids/${bidId}`);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'extracted':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'processing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'extracted':
        return <FileCheck className="h-3.5 w-3.5" />;
      case 'processing':
        return <Loader2 className="h-3.5 w-3.5 animate-spin" />;
      case 'failed':
        return <AlertCircle className="h-3.5 w-3.5" />;
      default:
        return null;
    }
  };

  // Calculate pagination info
  const totalPages = Math.ceil(filteredBids.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, filteredBids.length);
  const currentBids = filteredBids.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="h-10 w-10 text-fxer-blue animate-spin mb-4" />
          <p className="text-gray-500">Loading bids...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="flex flex-col gap-6">
        {/* Header with Title and Upload Button */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-fxer-blue">Contractor Bids</h1>
            <p className="text-gray-500 mt-1">Upload and analyze contractor bid documents</p>
          </div>
          <Button
            onClick={() => setIsUploadDialogOpen(true)}
            className="bg-fxer-blue hover:opacity-90"
          >
            <Plus className="mr-2 h-4 w-4" />
            Upload Bid
          </Button>
        </div>

        {/* Search and Filter Bar */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              type="text"
              placeholder="Search bids..."
              value={searchQuery}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="relative" ref={dropdownRef}>
            <Button
              variant="outline"
              className="flex items-center gap-2"
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              <Filter className="h-4 w-4" />
              <span>Sort: {sortOption === 'newest' ? 'Newest' : 'Oldest'}</span>
            </Button>

            {dropdownOpen && (
              <div className="absolute right-0 z-10 mt-2 w-40 rounded-md border border-gray-200 bg-white shadow-lg">
                <div className="p-1">
                  <button
                    className={`flex w-full items-center rounded-sm px-3 py-2 text-sm ${sortOption === 'newest' ? 'bg-gray-100 text-fxer-blue font-medium' : ''}`}
                    onClick={() => {
                      setSortOption('newest');
                      setDropdownOpen(false);
                    }}
                  >
                    Newest First
                  </button>
                  <button
                    className={`flex w-full items-center rounded-sm px-3 py-2 text-sm ${sortOption === 'oldest' ? 'bg-gray-100 text-fxer-blue font-medium' : ''}`}
                    onClick={() => {
                      setSortOption('oldest');
                      setDropdownOpen(false);
                    }}
                  >
                    Oldest First
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Bid List */}
        {filteredBids.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              {searchQuery ? (
                <>
                  <p className="text-gray-500 mb-2">No bids match your search</p>
                  <Button variant="outline" onClick={() => setSearchQuery('')}>
                    Clear Search
                  </Button>
                </>
              ) : (
                <>
                  <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-xl font-medium mb-2">No bids yet</h3>
                  <p className="text-gray-500 mb-6">
                    Upload your first contractor bid to get started with automated extraction and analysis
                  </p>
                  <Button
                    onClick={() => setIsUploadDialogOpen(true)}
                    className="bg-fxer-blue hover:opacity-90"
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Upload Your First Bid
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Count display */}
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Showing {startIndex + 1} to {endIndex} of {filteredBids.length} bids
              </div>

              {/* Page size selector */}
              <div className="relative" ref={pageSizeDropdownRef}>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-sm"
                  onClick={() => setPageSizeDropdownOpen(!pageSizeDropdownOpen)}
                >
                  {pageSize} per page
                </Button>

                {pageSizeDropdownOpen && (
                  <div className="absolute right-0 z-10 mt-2 w-32 rounded-md border border-gray-200 bg-white shadow-lg">
                    <div className="p-1">
                      {[10, 25, 50].map((size) => (
                        <button
                          key={size}
                          className={`flex w-full items-center rounded-sm px-3 py-2 text-sm ${pageSize === size ? 'bg-gray-100 text-fxer-blue font-medium' : ''}`}
                          onClick={() => {
                            setPageSize(size);
                            setCurrentPage(1);
                            setPageSizeDropdownOpen(false);
                          }}
                        >
                          {size} per page
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Bid cards */}
            <div className="grid grid-cols-1 gap-4">
              {currentBids.map((bid) => (
                <Card
                  key={bid.id}
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => handleBidClick(bid.id)}
                >
                  <CardContent className="p-5">
                    <div className="flex justify-between items-start">
                      <div className="flex-grow">
                        <h3 className="text-lg font-medium mb-1">Bid {bid.id.substring(0, 8)}</h3>
                        <div className="flex items-center gap-4 text-gray-500 text-sm">
                          <div className="flex items-center">
                            <Calendar className="h-3.5 w-3.5 mr-1" />
                            <span>Uploaded {new Date(bid.date).toLocaleDateString()}</span>
                          </div>
                          <Badge className={getStatusBadgeClass(bid.status)}>
                            <span className="flex items-center gap-1">
                              {getStatusIcon(bid.status)}
                              {bid.status}
                            </span>
                          </Badge>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        className="rounded-full h-8 w-8 p-0"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleBidClick(bid.id);
                        }}
                      >
                        <FileText className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                <span className="text-sm text-gray-600 px-4">
                  Page {currentPage} of {totalPages}
                </span>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        )}

        <BidUploadComponent
          isOpen={isUploadDialogOpen}
          onClose={() => setIsUploadDialogOpen(false)}
          onBidUploaded={handleBidUploaded}
        />
      </div>
    </div>
  );
};
